import { createApi } from '@reduxjs/toolkit/query/react'

import AdminStat from '~/redux/models/admin-stat'
import { TutorOnDemandStat } from '~/panels/admin/pages/AdminPanel/AdminPanel.types'
import { Preference, Tutor, Student } from '~/types/tutoring-api'
import { IUser } from '~/types/user-info-types'
import { transformPreference } from '~/utils'
import { apiPrivate } from '~/redux/utils/hom-api'
// import { homApi } from '~/redux/apis/hom'

import { ADMIN_API_TAGS, SCHOOL_ADMIN_API_TAGS } from '~/redux/constants/tags.const'

import { StripeProductGeolocation } from '~/types/stripe-subscription'

import { AuthPermissions } from '~/shared/types/permissions.types'

import {
  PageRequest,
  CountryCount,
  UserTypeCount,
  StripeProduct,
  SubscriptionPermission,
  FeatureFreeAccessDuration,
  FeatureFreeAccessDurationEdit,
  TimeTrackingViewKeyRule,
} from './types'

export const adminApi = createApi({
  reducerPath: 'adminApi',
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 1,
  baseQuery: apiPrivate,
  tagTypes: [
    'Admin',
    'Tutor',
    'StripeGeoProduct',
    'SubscriptionPermission',
    'FeatureFreeAccessDuration',
    'TimeTrackingViewKeyRule',
    SCHOOL_ADMIN_API_TAGS.School,
    SCHOOL_ADMIN_API_TAGS.Classroom,
    SCHOOL_ADMIN_API_TAGS.Teacher,
  ],

  // export const adminApi = homApi.injectEndpoints({
  endpoints: (builder) => ({
    assignTutor: builder.mutation<{ tutor: Tutor }, { tutorId: number; studentId: number }>({
      query: (data) => ({
        url: `/v1/admin/mentors/assign`,
        method: 'PATCH',
        body: {
          mentorId: data.tutorId,
          studentId: data.studentId,
        },
      }),
      transformResponse(response: { mentor: Tutor }) {
        return {
          tutor: response.mentor,
        }
      },
    }),

    unassignTutor: builder.mutation<void, { tutorId: number; studentId: number }>({
      query: (data) => ({
        url: `/v1/admin/mentors/unassign`,
        method: 'PATCH',
        body: {
          mentorId: data.tutorId,
          studentId: data.studentId,
        },
      }),
    }),

    createZoomAccount: builder.mutation<void, { userId: number }>({
      query: (data) => ({
        url: '/v1/admin/mentors/zoom-user',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [ADMIN_API_TAGS.Tutor],
    }),

    createStripeProductGeolocation: builder.mutation<void, StripeProductGeolocation>({
      query: (data) => ({
        url: '/v1/admin/stripe/products-geolocation',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [ADMIN_API_TAGS.StripeGeoProduct],
    }),

    deleteGeolocationProduct: builder.mutation<StripeProductGeolocation, { id: number }>({
      query: (data) => ({
        url: `/v1/admin/stripe/products-geolocation/${data.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [ADMIN_API_TAGS.StripeGeoProduct],
    }),

    getUserData: builder.query<
      {
        usersCount: number
        countries: CountryCount[]
        userTypes: UserTypeCount[]
      },
      {
        startTime?: string
        endTime?: string
      }
    >({
      query: (params) => ({
        url: '/v1/admin/users/data',
        method: 'GET',
        params,
      }),
    }),

    getTutorOnDemandStats: builder.query<
      AdminStat,
      {
        startTime?: string
        endTime?: string
      }
    >({
      query: (params) => ({
        url: '/v1/admin/statistics/mentor-on-demand',
        method: 'GET',
        params,
      }),
      transformResponse(response: TutorOnDemandStat) {
        return new AdminStat(response)
      },
    }),

    getStudents: builder.query<{ students: Student[]; studentsCount: number }, PageRequest & { campaignId?: string }>({
      query: (params) => ({
        url: '/v1/admin/students',
        method: 'GET',
        params,
      }),
    }),

    getUsers: builder.query<{ users: IUser[]; usersCount: number }, PageRequest & { role: string }>({
      query: (params) => ({
        url: '/v1/admin/users',
        method: 'GET',
        params,
      }),
    }),

    getTutors: builder.query<{ tutors: Tutor[]; tutorsCount: number }, PageRequest>({
      query: (params) => ({
        url: '/v1/admin/mentors',
        method: 'GET',
        params,
      }),
      transformResponse(response: { mentors: Tutor[]; mentorsCount: number }) {
        return {
          ...response,
          tutors: response.mentors,
          tutorsCount: response.mentorsCount,
        }
      },
      providesTags: [ADMIN_API_TAGS.Tutor],
    }),

    getTutor: builder.query<Tutor, { tutorId: number }>({
      query: (params) => ({
        url: `/v1/admin/mentors/${params.tutorId}`,
        method: 'GET',
      }),
      transformResponse(response: Tutor<'from-api-response'>) {
        return {
          ...response,
          preference: transformPreference(response?.preference),
        }
      },
    }),

    getStripeProductList: builder.query<StripeProduct[], void>({
      query: () => ({
        url: '/v1/admin/stripe/products',
        method: 'GET',
      }),
    }),

    getStripeGeoProductList: builder.query<any, void>({
      query: () => ({
        url: '/v1/admin/stripe/products-geolocation',
        method: 'GET',
      }),
      providesTags: [ADMIN_API_TAGS.StripeGeoProduct],
    }),

    updateStripeGeolocationPriceListOrder: builder.mutation<void, Array<{ id: string }>>({
      query: (data) => ({
        url: `/v1/admin/stripe/products-geolocation-display-order`,
        method: 'PATCH',
        body: {
          order: data,
        },
      }),
      invalidatesTags: [ADMIN_API_TAGS.StripeGeoProduct],
    }),

    editTutor: builder.mutation<
      { tutor: Tutor },
      { tutorId: number; preference: Partial<Preference>; user: Partial<IUser> }
    >({
      query(body) {
        const { tutorId, preference, user } = body
        return {
          url: `/v1/admin/mentors/${tutorId}`,
          method: 'PATCH',
          body: { preference, user },
        }
      },
    }),

    createSubscriptionPermission: builder.mutation<void, SubscriptionPermission>({
      query: (data) => ({
        url: '/v1/admin/subscription/permissions',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [ADMIN_API_TAGS.SubscriptionPermission],
    }),

    getSubscriptionPermissionsList: builder.query<any, void>({
      query: () => ({
        url: '/v1/admin/subscription/permissions',
        method: 'GET',
      }),
      providesTags: [ADMIN_API_TAGS.SubscriptionPermission],
    }),

    deleteSubscriptionPermission: builder.mutation<SubscriptionPermission, { id: number }>({
      query: (data) => ({
        url: `/v1/admin/subscription/permissions/${data.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [ADMIN_API_TAGS.SubscriptionPermission],
    }),

    createFeatureFreeAccessDuration: builder.mutation<void, FeatureFreeAccessDuration>({
      query: (data) => ({
        url: '/v1/admin/subscription/feature-free-access-duration',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [ADMIN_API_TAGS.FeatureFreeAccessDuration],
    }),

    editFeatureFreeAccessDuration: builder.mutation<void, FeatureFreeAccessDurationEdit>({
      query: (data) => ({
        url: '/v1/admin/subscription/feature-free-access-duration',
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [ADMIN_API_TAGS.FeatureFreeAccessDuration],
    }),

    getFeatureFreeAccessDurationsList: builder.query<any, void>({
      query: () => ({
        url: '/v1/admin/subscription/feature-free-access-duration',
        method: 'GET',
      }),
      providesTags: [ADMIN_API_TAGS.FeatureFreeAccessDuration],
    }),

    getTimeTrackingViewKeyRules: builder.query<TimeTrackingViewKeyRule[], void>({
      query: () => ({
        url: '/v1/admin/time-tracking/view-key-rules',
        method: 'GET',
      }),
      providesTags: [ADMIN_API_TAGS.TimeTrackingViewKeyRule],
    }),

    addTimeTrackingViewKeyRule: builder.mutation<
      TimeTrackingViewKeyRule,
      { viewKey: string; label: string; isExcluded: boolean }
    >({
      query: (data) => ({
        url: '/v1/admin/time-tracking/view-key-rules',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [ADMIN_API_TAGS.TimeTrackingViewKeyRule],
    }),

    setTimeTrackingViewKeyRule: builder.mutation<TimeTrackingViewKeyRule, { id: number; isExcluded: boolean }>({
      query: (data) => ({
        url: '/v1/admin/time-tracking/view-key-rules',
        method: 'PATCH',
        body: data,
      }),
      // Optimistic: the switch moves on click and snaps back if the save fails.
      async onQueryStarted({ id, isExcluded }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          adminApi.util.updateQueryData('getTimeTrackingViewKeyRules', undefined, (rules) =>
            rules.map((rule) => (rule.id === id ? { ...rule, isExcluded } : rule)),
          ),
        )

        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
      // Not on failure: RTK invalidates on rejected mutations too, and that refetch would race the undo.
      invalidatesTags: (_result, error) => (error ? [] : [ADMIN_API_TAGS.TimeTrackingViewKeyRule]),
    }),

    deleteTimeTrackingViewKeyRule: builder.mutation<{ success: boolean }, { id: number }>({
      query: (data) => ({
        url: `/v1/admin/time-tracking/view-key-rules/${data.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [ADMIN_API_TAGS.TimeTrackingViewKeyRule],
    }),

    deleteFeatureFreeAccessDuration: builder.mutation<FeatureFreeAccessDuration, { id: number }>({
      query: (data) => ({
        url: `/v1/admin/subscription/feature-free-access-duration/${data.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [ADMIN_API_TAGS.FeatureFreeAccessDuration],
    }),

    softDeleteUser: builder.mutation<
      { type: string; data: { userId: number; anonymized: boolean } },
      { userId: number }
    >({
      query: (data) => ({
        url: `/v1/admin/users/${data.userId}/anonymize`,
        method: 'POST',
      }),
    }),

    hardDeleteUser: builder.mutation<{ type: string; data: { userId: number; deleted: boolean } }, { userId: number }>({
      query: (data) => ({
        url: `/v1/admin/users/${data.userId}`,
        method: 'DELETE',
      }),
    }),

    setUsersPermissions: builder.mutation<void, { userId: number; permissions: string[] }>({
      query: (data) => ({
        url: 'v1/auth/permissions/set',
        method: 'POST',
        body: data,
      }),
    }),

    getUserPermissionsById: builder.query<{ permissions: AuthPermissions[] }, { userId: number }>({
      query: (params) => ({
        url: `v1/auth/permissions/get/${params.userId}`,
        method: 'GET',
      }),
    }),

    getUserPermissions: builder.query<{ permissions: AuthPermissions[] }, void>({
      query: () => ({
        url: 'v1/auth/permissions/get',
        method: 'GET',
      }),
    }),
  }),
})

export const {
  useGetTutorQuery,
  useGetStudentsQuery,
  useGetTutorsQuery,
  useEditTutorMutation,
  useAssignTutorMutation,
  useUnassignTutorMutation,
  useCreateStripeProductGeolocationMutation,
  useUpdateStripeGeolocationPriceListOrderMutation,
  useGetUsersQuery,
  useGetUserDataQuery,
  useCreateZoomAccountMutation,
  useDeleteGeolocationProductMutation,
  useGetTutorOnDemandStatsQuery,
  useGetStripeProductListQuery,
  useGetStripeGeoProductListQuery,
  useCreateSubscriptionPermissionMutation,
  useGetSubscriptionPermissionsListQuery,
  useDeleteSubscriptionPermissionMutation,
  useGetTimeTrackingViewKeyRulesQuery,
  useAddTimeTrackingViewKeyRuleMutation,
  useSetTimeTrackingViewKeyRuleMutation,
  useDeleteTimeTrackingViewKeyRuleMutation,
  useCreateFeatureFreeAccessDurationMutation,
  useEditFeatureFreeAccessDurationMutation,
  useGetFeatureFreeAccessDurationsListQuery,
  useDeleteFeatureFreeAccessDurationMutation,
  useSoftDeleteUserMutation,
  useHardDeleteUserMutation,
  useSetUsersPermissionsMutation,
  useGetUserPermissionsQuery,
  useGetUserPermissionsByIdQuery,
} = adminApi
